import { assert } from '@varos/util-typescript';
import { useQueryClient } from '@tanstack/react-query';
import { getCountLookupQueryKey } from '../count';
const RECORD_LIST_QUERY_KEY = ['research', 'record', 'find'];
const RECORD_LOOKUP_QUERY_KEY = ['research', 'record', 'lookup'];
export function getRecordListQueryKey(props) {
    return [...RECORD_LIST_QUERY_KEY, props.collection];
}
export function getRecordLookupKey(contentId) {
    return [...RECORD_LOOKUP_QUERY_KEY, { contentId }];
}
export function createRecordRepository(config) {
    const { repository, client } = config;
    const DEFAULT_LIMIT = 50;
    return {
        usePrefetchFind(context) {
            const queryClient = useQueryClient();
            return {
                trigger(props) {
                    return queryClient.prefetchQuery({
                        queryKey: getRecordListQueryKey(props),
                        async queryFn() {
                            const { ids, ...rest } = props;
                            if (ids) {
                                // TODO move to backend
                                const response = await Promise.all(ids.map(async (id) => {
                                    return await client.user.content.retrieve(id);
                                }));
                                return response;
                            }
                            const response = await client.user.content.list({
                                ...rest,
                                limit: props.limit ?? DEFAULT_LIMIT,
                            });
                            return response.data;
                        },
                    });
                },
            };
        },
        useFind(context, props, options) {
            // NOTE we dont use the higher-level record client for displaying a list of records to
            // avoid N+1 loading the snippets and other related properties
            const queryClient = useQueryClient();
            const query = repository.useQuery({
                ...options,
                queryKey: getRecordListQueryKey({
                    collection: props.collection,
                }),
                async queryFn() {
                    const { ids, ...rest } = props;
                    if (ids) {
                        // TODO move to backend
                        const response = await Promise.all(ids.map(async (id) => {
                            return await client.user.content.retrieve(id);
                        }));
                        return response;
                    }
                    const { data: records } = await client.user.content.list({
                        ...rest,
                        limit: props.limit ?? DEFAULT_LIMIT,
                    });
                    return records;
                },
                onSuccess(data) {
                    for (const record of data) {
                        const queryKey = getRecordLookupKey(record.id);
                        queryClient.setQueryData(queryKey, record);
                    }
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getRecordLookupKey(props.contentId),
                async queryFn() {
                    const record = await config.client.user.content.retrieve(props.contentId);
                    return record;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(payload) {
                    const response = await config.client.user.content.create(payload);
                    return response;
                },
                onSuccess(data) {
                    assert(data.collection, 'expected collection');
                    const queryKey = getRecordListQueryKey({
                        collection: data.collection,
                    });
                    // update local data
                    queryClient.setQueryData(queryKey, (old) => [data, ...(old ?? [])]);
                    queryClient.setQueryData(getRecordLookupKey(data.id), data);
                    queryClient.setQueryData(getCountLookupQueryKey({
                        kind: 'collection.total',
                        scope: data.collection,
                    }), (old) => {
                        return old
                            ? {
                                ...old,
                                value: old.value + 1,
                            }
                            : old;
                    });
                    // invalidate queries
                    queryClient.invalidateQueries({ queryKey });
                    queryClient.invalidateQueries({
                        queryKey: getRecordLookupKey(data.id),
                    });
                    queryClient.invalidateQueries({
                        queryKey: getCountLookupQueryKey({
                            kind: 'collection.total',
                            scope: data.collection,
                        }),
                    });
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn([id, payload]) {
                    const response = await config.client.user.content.update(id, payload);
                    return response;
                },
                async onSuccess(data) {
                    assert(data.collection, 'expected collection');
                    // Refetch all list queries - they will automatically update lookup queries via onSuccess
                    await queryClient.refetchQueries({
                        queryKey: getRecordListQueryKey({
                            collection: data.collection,
                        }),
                    });
                },
            });
        },
        useArchive(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(payload) {
                    const response = await client.user.content.archive(payload.contentId);
                    return response;
                },
                onSuccess(data) {
                    assert(data.collection, 'expected collection');
                    // update local data
                    queryClient.setQueriesData({
                        queryKey: getRecordListQueryKey({
                            collection: data.collection,
                        }),
                        exact: true,
                    }, (old) => old?.filter((item) => item.id !== data.id));
                    queryClient.setQueryData(getCountLookupQueryKey({
                        kind: 'collection.total',
                        scope: data.collection,
                    }), (old) => {
                        return old ? { ...old, value: old.value - 1 } : old;
                    });
                    // invalidate queries
                    queryClient.refetchQueries({
                        queryKey: getRecordListQueryKey({
                            collection: data.collection,
                        }),
                    });
                    queryClient.removeQueries({
                        queryKey: getRecordLookupKey(data.id),
                    });
                },
            });
        },
    };
}
//# sourceMappingURL=recordRepository.js.map